import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { SeatingArea } from '../models/SeatingArea'
import { seatingAreaSchema } from '../models/schema'
import {
  createSeatingArea as createSeatingAreaAPI,
  deleteSeatingArea as deleteSeatingAreaAPI,
  fetchSeatingAreaById,
  fetchSeatingAreas,
  updateSeatingArea as updateSeatingAreaAPI,
} from '../services/SeatingAreaService'
import { AppDispatch, RootState } from './store'

const initialState: SBState<SeatingArea> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'seatingArea',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getSeatingAreasSuccess,
  setQuery: setSeatingAreaQuery,
  reset: resetSeatingAreaState,
  resetQueryAndIds: resetSeatingAreaQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: SeatingArea }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectSeatingAreas = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedSeatingArea = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectSeatingAreaById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectSeatingAreasByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedSeatingArea =
  (seatingArea: SeatingArea | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<SeatingArea>(seatingArea))
    )

export const getSeatingAreas = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchSeatingAreas(params),
    [seatingAreaSchema],
    slice.actions
  )

export const createSeatingArea = (seatingArea: SeatingArea) =>
  SBAPI.CreateDispatched(
    createSeatingAreaAPI(seatingArea),
    seatingAreaSchema,
    slice.actions
  )

export const getSeatingAreaWithId = (id: number) =>
  SBAPI.FetchDispatched<SeatingArea>(
    fetchSeatingAreaById(id),
    seatingAreaSchema,
    slice.actions
  )

export const updateSeatingArea = (seatingArea: SeatingArea) =>
  SBAPI.UpdateDispatched<SeatingArea>(
    updateSeatingAreaAPI(seatingArea),
    seatingArea,
    slice.actions
  )

export const deleteSeatingArea = (seatingArea: SeatingArea) =>
  SBAPI.DestroyDispatched<SeatingArea>(
    deleteSeatingAreaAPI(seatingArea),
    seatingArea,
    slice.actions
  )
